import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant } from '@makyoapp/makyo-ui'

interface WorkflowFullscreenModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  onTitleRename?: (newTitle: string) => void
  children: React.ReactNode
}

export const WorkflowFullscreenModal: React.FC<WorkflowFullscreenModalProps> = ({
  isOpen,
  onClose,
  title = 'Advanced Workflow Editor',
  onTitleRename,
  children,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editableTitle, setEditableTitle] = useState(title)
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Update editable title when title prop changes
  useEffect(() => {
    setEditableTitle(title)
  }, [title])

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        if (isEditingTitle) {
          // Cancel editing and revert to original title
          setEditableTitle(title)
          setIsEditingTitle(false)
        } else {
          onClose()
        }
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose, isEditingTitle, title])

  const handleTitleDoubleClick = () => {
    if (onTitleRename) {
      setIsEditingTitle(true)
    }
  }

  const handleTitleSave = () => {
    if (onTitleRename && editableTitle.trim()) {
      onTitleRename(editableTitle.trim())
    } else {
      // Revert to original title if empty
      setEditableTitle(title)
    }
    setIsEditingTitle(false)
  }

  const handleTitleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleTitleSave()
    } else if (event.key === 'Escape') {
      setEditableTitle(title)
      setIsEditingTitle(false)
    }
  }

  const handleTitleBlur = () => {
    handleTitleSave()
  }

  if (!isOpen) return null

  return createPortal(
    <div className='fixed inset-0 z-[299] flex items-center justify-center'>
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />

      {/* Modal Container */}
      <div className='relative w-full h-full bg-white dark:bg-slate-900 flex flex-col'>
        {/* Header */}
        <div className='flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900'>
          <div className='flex-1 min-w-0'>
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type='text'
                value={editableTitle}
                onChange={(e) => setEditableTitle(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                onBlur={handleTitleBlur}
                className='w-full text-xl font-semibold text-gray-900 dark:text-white bg-transparent border-none outline-none rounded px-2 py-1'
                placeholder='Enter title...'
              />
            ) : (
              <h2
                className='text-xl font-semibold text-gray-900 dark:text-white cursor-pointer rounded px-2 py-1'
                onDoubleClick={handleTitleDoubleClick}
                title={onTitleRename ? 'Double-click to edit title' : undefined}
              >
                {editableTitle}
              </h2>
            )}
          </div>
          <Button
            variant={ButtonVariant.Ghost}
            onClick={onClose}
            icon={<MaterialIcon icon='close' size={24} />}
            className='p-0! hover:bg-transparent! cursor-pointer'
          />
        </div>

        {/* Content */}
        <div className='flex-1'>{children}</div>
      </div>
    </div>,
    document.body
  )
}
